import { Component, OnInit, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../Services/api.service';
import { GlobalService } from '../../Services/global.service';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-tracking',
  templateUrl: './tracking.component.html',
  styleUrls: ['./tracking.component.scss']
})
export class TrackingComponent implements OnInit {
  shortId: string = '';
  orderTrack: any = {};
  isLoading: boolean = true;
  companyURL: string = '';

  // Driver review properties
  rating: number = 0;
  reviewComment: string = '';
  hoveredRating: number = 0;
  showReviewForm: boolean = false;
  isSubmitting: boolean = false;
  reviewSubmitted: boolean = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private route: ActivatedRoute,
    private router: Router,
    private callMethod: ApiService,
    private global: GlobalService,
    private titleService: Title
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.shortId = params['id'] || '';
      if (this.shortId) {
        this.trackData();
      }
    });
  }

  trackData(): void {
    this.isLoading = true;
    this.callMethod.getSingleTaskTask({ 'shortId': this.shortId }).subscribe((result: any) => {
      if (result.status == false) {
        this.router.navigateByUrl('/error/404');
        console.log('No task found *******************');
        return;
      } else {
        if (result.status) {
          this.orderTrack = result.data;
          this.setTitle(this.orderTrack.organizationName);
          this.setFavicon(environment.favIconBaseUrl + this.orderTrack.favIcon);
          this.companyURL = environment.companyLogoBaseUrl + this.orderTrack.companyLogo;

          let order_status = this.global.task_activity_status_string(result.data.task.taskStatus);
          this.orderTrack.orderStatus = order_status;

          // Show review form if task is completed
          if (this.orderTrack.orderStatus?.task_status == 9 || this.orderTrack.orderStatus?.task_status == 15) {
            this.showReviewForm = true;
          }

          this.isLoading = false;
        }
      }
    }, error => {
      console.error('Error fetching task data:', error);
      this.isLoading = false;
    });
  }

  setRating(rating: number): void {
    this.rating = rating;
  }

  setHoveredRating(rating: number): void {
    this.hoveredRating = rating;
  }

  clearHoveredRating(): void {
    this.hoveredRating = 0;
  }

  // Touch event handlers for mobile swipe functionality
  onTouchStart(event: TouchEvent): void {
    event.preventDefault();
    this.handleTouch(event);
  }

  onTouchMove(event: TouchEvent): void {
    event.preventDefault();
    this.handleTouch(event);
  }

  onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    // The rating is already set by handleTouch, just clear the hovered state
    this.hoveredRating = 0;
  }

  private handleTouch(event: TouchEvent): void {
    const touch = event.touches[0];
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    // Calculate the position of the touch relative to the star container
    const x = touch.clientX - rect.left;
    const width = rect.width;

    // Calculate which star is being touched (1-5)
    const starWidth = width / 5;
    let touchedStar = Math.ceil(x / starWidth);

    // Ensure the rating is within bounds (1-5)
    touchedStar = Math.max(1, Math.min(5, touchedStar));

    // Update both hovered and actual rating for immediate visual feedback
    this.hoveredRating = touchedStar;
    this.rating = touchedStar;
  }

  getReviewerName(): string {
    // Get driver name from orderTrack
    const driverName = this.orderTrack?.task?.driver?.driverName;
    return driverName || 'Guest User';
  }

  getReviewerInitial(): string {
    const name = this.getReviewerName();
    if (name && name !== 'Guest User') {
      return name.charAt(0).toUpperCase();
    }
    return 'G';
  }

  cancelReview(): void {
    this.rating = 0;
    this.reviewComment = '';
    this.hoveredRating = 0;
  }

  submitReview(): void {
    if (this.rating === 0) {
      alert('Please select a rating');
      return;
    }

    if (this.isSubmitting) {
      return; // Prevent multiple submissions
    }

    this.isSubmitting = true;

    // Prepare payload
    const reviewData: any = {
      taskId: this.orderTrack.task?.taskId,
      driverId: this.orderTrack.task?.driver?.driverId,
      driverRating: this.rating,
      companyId: this.orderTrack.companyId,
      parentUser: this.orderTrack.parentUserId
    };

    // Add reviewComment only if it exists
    if (this.reviewComment && this.reviewComment.trim()) {
      reviewData.reviewComment = this.reviewComment.trim();
    }

    console.log('Submitting review:', reviewData);

    this.callMethod.submitDriverRating(reviewData).subscribe(
      (result: any) => {
        console.log('Review submission response:', result);
        this.isSubmitting = false;

        if (result.status) {
          // Show success message
          this.reviewSubmitted = true;
          this.showReviewForm = false;

          // Show success notification (like Uber apps)
          // this.showSuccessMessage();
        } else {
          alert(result.message);
        }
      },
      (error: any) => {
        console.error('Error submitting review:', error);
        this.isSubmitting = false;
        alert('An error occurred while submitting your review. Please try again.');
      }
    );
  }

  showSuccessMessage(): void {
    // Create a success notification element (Uber-like style)
    const notification = document.createElement('div');
    notification.className = 'review-success-notification';
    notification.innerHTML = `
      <div class="success-icon">✓</div>
      <div class="success-message">
        <div class="success-title">Thank you!</div>
        <div class="success-text">Your review has been submitted successfully.</div>
      </div>
    `;

    document.body.appendChild(notification);

    // Trigger animation
    setTimeout(() => {
      notification.classList.add('show');
    }, 10);

    // Remove notification after 3 seconds
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }

  // Function to set a new title
  public setTitle(newTitle: string): void {
    this.titleService.setTitle(newTitle);
  }

  // Method to set a new favicon
  public setFavicon(iconUrl: string): void {
    const link: HTMLLinkElement | null = this.document.querySelector("link[rel*='icon']");
    if (link) {
      link.href = iconUrl;
    } else {
      const newLink: HTMLLinkElement = this.document.createElement('link');
      newLink.rel = 'icon';
      newLink.href = iconUrl;
      this.document.head.appendChild(newLink);
    }
  }
}

